// Resolve the "return here after auth" target from a raw candidate — either a `pathname + search` string
// read off `window.location`, or a TanStack relative `location.href`.
//
// Guarantees the result never points back at an auth page and never carries a nested `?redirect=`. Without
// this, a `redirect-login` action that fires while the user is ALREADY on /login (the global 401 handler in
// main.tsx and the route-error boundary both hard-reload to `/login?redirect=<current url>`) folds the whole
// current URL — which already contains a `?redirect=` — into a fresh, re-encoded `?redirect=` on every pass.
// That is the runaway `/login?redirect=%2Flogin%3Fredirect%3D%252Flogin…` accumulation.
//
// Returns undefined when there's no meaningful destination (already on an auth page with no inner target),
// so callers can omit the `redirect` param entirely rather than pointing it at /login.
//
// Pure (only the global URLSearchParams) so it's safe to import from the DOM-capture sites and unit-testable.
const AUTH_PATH_SUFFIXES = ['/login', '/register']

export function resolveRedirectTarget(candidate: string | null | undefined): string | undefined {
  let target = candidate ?? ''
  // Unwrap nested redirects: if the candidate is itself an auth page carrying a `redirect`, drill into it.
  // Normal flows resolve in a single step; the bound just guards against a pathological hand-crafted URL.
  for (let depth = 0; depth < 25; depth++) {
    if (!target) return undefined
    const queryStart = target.indexOf('?')
    const path = queryStart === -1 ? target : target.slice(0, queryStart)
    if (!AUTH_PATH_SUFFIXES.some((suffix) => path.endsWith(suffix))) return target
    // URLSearchParams.get decodes exactly one layer of percent-encoding, peeling off one nesting level.
    const query = queryStart === -1 ? '' : target.slice(queryStart + 1)
    const inner = new URLSearchParams(query).get('redirect')
    if (!inner) return undefined
    target = inner
  }
  return undefined
}
